import React, {Dispatch, SetStateAction, useEffect, useState, useCallback} from 'react'
import {FixedSizeList as List} from 'react-window'
import InfiniteLoader from 'react-window-infinite-loader'
import AutoSizer from 'react-virtualized-auto-sizer'
import {
  ColumnDef,
  ColumnFiltersState,
  FilterFn,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table'
import {RankingInfo, rankItem} from '@tanstack/match-sorter-utils'
import {useIntl} from 'react-intl'
import {toast} from 'react-toastify'
import {getRecordKeeperSourceStorageFiles} from '../../core/_requests'
import {RecordSync} from '../../core/_constants'
import {useAuth} from '../../../auth'
import {formatFileSize, getFileType} from '../../../../core/_util'
import {useDateFormat} from '../../../../hooks/useDateFormat'
import NoDataPage from '../../../widgets/components/NoDataPage'
import SkeletonRow from '../../../digital-signing/components/SkeletonRow'
import {Table} from 'react-bootstrap'
import { SelectedFileOrFolderId} from '../../core/_models'

// Type declarations
declare module '@tanstack/table-core' {
  interface FilterFns {
    fuzzy: FilterFn<unknown>
  }
  interface FilterMeta {
    itemRank: RankingInfo
  }
}

const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
  const itemRank = rankItem(row.getValue(columnId), value)
  addMeta({itemRank})
  return itemRank.passed
}

interface StorageDirectory {
  fileName: string
  role: string
  id: string
  name: string
  isFolder: boolean
  dateTime: string
  generatedBy: string
  lastModified: string
  modifiedTime: string
  size: string
}

interface StorageDirectoryTableProps {
  sourceType: RecordSync
  sourceId: string
  selectedFileOrFolderId: SelectedFileOrFolderId | null
  setSelectedFileOrFolderId: Dispatch<SetStateAction<SelectedFileOrFolderId | null>>
  setParentFolder: Dispatch<SetStateAction<{id: string; name: string} | null>>
  folderPath: {id: string; name: string}[] | []
  setFolderPath: Dispatch<SetStateAction<{id: string; name: string}[] | []>>
  openedFolderId: string | null
  setOpenedFolderId: Dispatch<SetStateAction<string | null>>
}

// Memoized Row Component
const Row = React.memo(
  ({
    index,
    style,
    data,
  }: {
    index: number
    style: React.CSSProperties
    data: {
      table: any
      selectedFileOrFolderId: SelectedFileOrFolderId | null
      setSelectedFileOrFolderId: (value: SelectedFileOrFolderId | null) => void
      handleFolderClick: (id: string, name: string) => void
      setItems: Dispatch<SetStateAction<StorageDirectory[] | []>>
      setNextPageToken: Dispatch<SetStateAction<string | null>>
      setHasNextPage: Dispatch<SetStateAction<boolean>>
    }
  }) => {
    const {
      table,
      selectedFileOrFolderId,
      setSelectedFileOrFolderId,
      handleFolderClick,
      setItems,
      setNextPageToken,
      setHasNextPage,
    } = data
    const row = table.getRowModel().rows[index]

    // Define callbacks before any conditional returns
    const handleClick = useCallback(
      (e: React.MouseEvent) => {
        if (!row) return
        e.stopPropagation()
        setSelectedFileOrFolderId({
          isFolder: row.original.isFolder,
          id: row.original.id,
          name: row.original.name,
        })
      },
      [row, setSelectedFileOrFolderId]
    )

    const handleDoubleClick = useCallback(
      (e: React.MouseEvent) => {
        if (e.detail <= 1) return
        if (!row) return
        e.stopPropagation()
        if (row.original.isFolder) {
          setItems([])
          setNextPageToken(null)
          setHasNextPage(true)
          handleFolderClick(row.original.id, row.original.name)
        }
      },
      [row, handleFolderClick, setItems, setNextPageToken, setHasNextPage]
    )

    return (
      <div
        role='row'
        style={{
          ...style,
          display: 'flex',
          borderBottom: '1px solid #eee',
          cursor: 'pointer',
          color: row.original.id === selectedFileOrFolderId?.id ? '#004a77' : '',
          backgroundColor: row.original.id === selectedFileOrFolderId?.id ? '#c2e7ff' : '',
          transition: 'background-color 0.2s ease',
          width: '100%',
          userSelect: 'none',
        }}
        onMouseDown={handleDoubleClick}
        onClick={handleClick}
        className='storage-row'
      >
        {row.getVisibleCells().map((cell: any) => {
          return (
            <div
              key={cell.id}
              style={{
                flex: cell.column.id === 'name' ? 2 : 1,
                padding: '12px',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </div>
          )
        })}
      </div>
    )
  }
)

export const StorageDirectoryTable: React.FC<StorageDirectoryTableProps> = ({
  sourceType,
  sourceId,
  selectedFileOrFolderId,
  setSelectedFileOrFolderId,
  setParentFolder,
  folderPath,
  setFolderPath,
  openedFolderId,
  setOpenedFolderId,
}) => {
  const {formatMessage} = useIntl()
  const {companyId} = useAuth()
  const {getDateValue} = useDateFormat()

  // State Management
  const [loading, setLoading] = useState(false)
  const [hasNextPage, setHasNextPage] = useState(true)
  const [items, setItems] = useState<StorageDirectory[]>([])
  const [nextPageToken, setNextPageToken] = useState<string | null>(null)
  const [pageSize] = useState(20)

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [globalFilter, setGlobalFilter] = useState('')
  const [sorting, setSorting] = useState<SortingState>([])

  // Column Definitions
  const columns = React.useMemo<ColumnDef<StorageDirectory>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        size: 250,
        cell: (info) => (
          <div className='d-flex align-items-center gap-2'>
            {info.row.original.isFolder ? (
              <i className='fa-solid fa-folder font-size-24 text-warning'></i>
            ) : (
              <img
                src={getFileType(info.row.original.name)}
                alt={info.row.original.name}
                style={{height: '25px', width: '25px'}}
              />
            )}
            <span className='font-size-13'>{info.getValue() as string}</span>
          </div>
        ),
      },
      {
        accessorKey: 'modifiedTime',
        header: 'Last Modified',
        size: 150,
        cell: (info) => (
          <span className='font-size-13'>
            {info.getValue() ? getDateValue(info.getValue() as string) : '-'}
          </span>
        ),
      },
      {
        accessorKey: 'size',
        header: 'Size',
        size: 100,
        cell: (info) => (
          <span className='font-size-13'>
            {info.row.original.isFolder ? '-' : formatFileSize(info.getValue() as string)}
          </span>
        ),
      },
    ],
    [getDateValue]
  )

  // Table Instance
  const table = useReactTable({
    data: items,
    columns,
    state: {
      sorting,
      columnFilters,
      globalFilter,
    },
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  // Navigation Handlers
  const handleFolderClick = useCallback((folderId: string, folderName: string) => {
    setOpenedFolderId(folderId)
    setFolderPath((prev) => [...prev, {id: folderId, name: folderName}])
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleBreadcrumbClick = useCallback(
    (index: number) => {
      setItems([])
      setNextPageToken(null)
      setHasNextPage(true)
      if (index === -1) {
        setOpenedFolderId('rootFolder')
        setFolderPath([])
      } else {
        const newPath = folderPath.slice(0, index + 1)
        setFolderPath(newPath)
        setOpenedFolderId(newPath[newPath.length - 1].id)
      }
      setSelectedFileOrFolderId(null)
    },
    [folderPath] // eslint-disable-line react-hooks/exhaustive-deps
  )

  useEffect(() => {
    if (selectedFileOrFolderId?.isFolder) {
      const selectedFolder = items.find((item) => item.id === selectedFileOrFolderId.id)
      if (selectedFolder) {
        setParentFolder({
          id: selectedFolder.id,
          name: selectedFolder.name,
        })
      }
    }
  }, [selectedFileOrFolderId, items]) // eslint-disable-line react-hooks/exhaustive-deps

  // Data Loading Functions
  const loadMoreItems = async (index?: number) => {
    if (!index) {
      setLoading(true)
    }

    try {
      const {
        data: {success, data: responseData, errors},
      } = await getRecordKeeperSourceStorageFiles(
        pageSize,
        nextPageToken,
        sourceType,
        sourceId,
        companyId!,
        openedFolderId
      )

      if (success) {
        if (index) {
          setItems((prevItems) => [...prevItems, ...responseData.items])
        } else {
          setItems(responseData.items)
        }

        setNextPageToken(responseData.nextPageToken)
        setHasNextPage(!!responseData.nextPageToken)
      } else {
        toast.error(errors[0])
      }
    } catch (error) {
      console.error('Error loading more items:', error)
    } finally {
      setLoading(false)
    }
  }

  const isItemLoaded = useCallback(
    (index: number) => {
      return !hasNextPage || index < items.length
    },
    [hasNextPage, items.length]
  )

  // Reset data when folder changes
  useEffect(() => {
    loadMoreItems()
  }, [openedFolderId]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className='storage-directory-container'>
      {/* Breadcrumb */}
      <div className='bg-white p-4 mb-2'>
        <div className='d-flex align-items-center gap-2'>
          {folderPath.length > 0 && (
            <span
              onClick={() => handleBreadcrumbClick(-1)}
              className='font-size-13 cursor-pointer text-primary'
            >
              {formatMessage({id: 'Home'})}
            </span>
          )}

          {folderPath.map((folder, index) => (
            <React.Fragment key={folder.id}>
              {(index > 0 || folderPath.length > 0) && (
                <span className='font-size-13 text-dark'>{'>'}</span>
              )}
              <span
                className={`font-size-13 cursor-pointer ${
                  index === folderPath.length - 1 ? 'text-dark' : 'text-primary'
                }`}
                onClick={() => {
                  if (index < folderPath.length - 1) {
                    handleBreadcrumbClick(index)
                  }
                }}
              >
                {folder.name}
              </span>
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className='bg-white rounded' style={{height: 'calc(100vh - 250px)'}}>
        {!loading && items.length === 0 ? (
          <div className='my-5 mx-6'>
            <NoDataPage width={'w-0'} />
          </div>
        ) : (
          <Table className='w-100 h-100' >
            {/* Table Header */}
            <thead
              className='d-flex font-weight-500 border-bottom pb-2 px-3 position-sticky top-0 bg-white'
              style={{height: '40px', zIndex: 1}}
            >
              {table.getFlatHeaders().map((header) => (
                <div
                  key={header.id}
                  style={{flex: header.id === 'name' ? 2 : 1}}
                  className={header.column.getCanSort() ? 'cursor-pointer select-none' : ''}
                  onClick={header.column.getToggleSortingHandler()}
                >
                  {flexRender(header.column.columnDef.header, header.getContext())}
                  {{
                    asc: ' ▲',
                    desc: ' ▼',
                  }[header.column.getIsSorted() as string] ?? null}
                </div>
              ))}
            </thead>

            {/* Virtualized List */}
            <tbody className='h-100'>
              {loading ? (
                <>
                  <SkeletonRow count={4} />
                </>
              ) : (
                <div className='source-table-scroll' style={{height: 'calc(100% - 40px)'}}>
                  <AutoSizer defaultHeight={800} >
                    {({width}: {width: number}) => (
                      <InfiniteLoader
                        isItemLoaded={isItemLoaded}
                        itemCount={table.getRowModel().rows.length + (hasNextPage ? 1 : 0)}
                        loadMoreItems={loadMoreItems}
                        threshold={5}
                      >
                        {({onItemsRendered, ref}: {onItemsRendered: any; ref: any}) => (
                          <List
                            ref={ref}
                            height={275}
                            width={width}
                            itemCount={table.getRowModel().rows.length}
                            itemSize={50}
                            onItemsRendered={onItemsRendered}
                            itemData={{
                              table,
                              selectedFileOrFolderId,
                              setSelectedFileOrFolderId,
                              handleFolderClick,
                              setItems,
                              setNextPageToken,
                              setHasNextPage,
                            }}
                          >
                            {Row}
                          </List>
                        )}
                      </InfiniteLoader>
                    )}
                  </AutoSizer>
                </div>
              )}
            </tbody>
          </Table>
        )}
      </div>
    </div>
  )
}
